import {Icon} from "@client/components/Icon";
import {HModal} from "@client/components/Modal";
import {NavigationTitle} from "@client/components/NavigationTitle";
import {useWorkoutData} from "@client/features/workouts";
import * as forms from "@client/forms";
import * as analytics from "@client/shared/analytics";
import * as typography from "@client/shared/typography";
import {useAppDispatch} from "@client/store";
import {server} from "@reactivated";
import * as React from "react";

interface Props {
    readOnly?: true;
    selectLabel: string;
    onSelectQuestionType: (
        questionType: server.journal.workouts.QuestionType.output,
    ) => void;
    disableCreate?: true;
    children: (props: {showQuestionTypeSelector: () => void}) => React.ReactElement;
}

export function QuestionTypeSelector(props: Props) {
    const dispatch = useAppDispatch();
    const workouts = useWorkoutData();
    const questionTypes = workouts?.question_types ?? [];
    const [isOpen, setIsOpen] = React.useState(false);

    const questionTypeForm = forms.useForm(server.journal.workouts.QuestionTypeInput);
    const query = questionTypeForm.values.name.trim();
    const questionQuery = query;

    const filtered =
        query === ""
            ? questionTypes
            : questionTypes.filter((person) => {
                  return person.name.toLowerCase().includes(query.toLowerCase());
              });

    const exactMatch =
        query === "" ||
        questionTypes.filter(
            (person) => person.name.toLowerCase() == query.toLowerCase(),
        ).length == 1;

    const [route, setRoute] = React.useState<"list" | "create">("list");

    const createQuestionType = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const response = await forms.submitForm(questionTypeForm, (data) =>
            server.journal.workouts.create_question_type({
                ...data,
                uuid: crypto.randomUUID(),
            }),
        );

        if (response?.type == "success") {
            dispatch({type: "CREATED_QUESTION_TYPE", questionType: response.data});
            setIsOpen(false);
            props.onSelectQuestionType(response.data);
            void analytics.track("CREATE_QUESTION_TYPE", {
                question_name: response.data.name,
            });
        }
    };

    const listRoute = (
        <HModal.Content
            title={
                <div className="">
                    <forms.Widget
                        field={questionTypeForm.fields.name}
                        placeholder="Question name"
                    />
                </div>
            }
            content={
                <div className="flex flex-col divide-y divide-gray-200">
                    {filtered.map((questionType) => (
                        <div key={questionType.uuid} className="flex gap-2 px-2 py-3">
                            <h2 className="flex-1 text-[15px]">{questionType.name}</h2>
                            <div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        props.onSelectQuestionType(questionType);
                                        setIsOpen(false);
                                    }}
                                    className="rounded-md bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 focus-visible:outline-solid disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-500 disabled:shadow-none"
                                >
                                    {props.selectLabel}
                                </button>
                            </div>
                        </div>
                    ))}
                    {questionQuery != "" && filtered.length == 0 && (
                        <p>No existing questions found.</p>
                    )}
                </div>
            }
            actions={
                <div className="space-y-4">
                    {exactMatch == false && props.disableCreate != true && (
                        <typography.ImportantAction
                            onClick={() => {
                                setRoute("create");
                            }}
                            title={`Create question "${query}"`}
                        />
                    )}
                </div>
            }
        />
    );

    const createRoute = (
        <HModal.Content
            title={
                <>
                    <div>
                        <button onClick={() => setRoute("list")}>
                            <Icon large="medium" icon="custom-chevron-left">
                                <NavigationTitle title="Questions" />
                            </Icon>
                        </button>
                    </div>
                    <div className="-mt-2">
                        <div className="flex-1 text-center">
                            <h2>Create question</h2>
                        </div>
                    </div>
                </>
            }
            content={
                <form
                    id="create-question-type-form"
                    className="flex flex-col gap-4"
                    onSubmit={createQuestionType}
                >
                    <forms.Field field={questionTypeForm.fields.name} />
                </form>
            }
            actions={
                <div className="flex flex-col">
                    <button
                        form="create-question-type-form"
                        type="submit"
                        disabled={questionTypeForm.values.name == ""}
                        className="mt-2 rounded-sm bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 focus-visible:outline-solid disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-500 disabled:shadow-none"
                    >
                        Create
                    </button>
                </div>
            }
        />
    );

    return (
        <>
            {props.children({
                showQuestionTypeSelector: () => {
                    questionTypeForm.reset();
                    setRoute("list");
                    setIsOpen(true);
                },
            })}
            <HModal isOpen={isOpen} setOpen={setIsOpen}>
                {route == "list" ? listRoute : createRoute}
            </HModal>
        </>
    );
}
